import type { IconProp, WithOptionalTestId } from "@/theme"
import { COLORS, RADII, SPACING, getFontName } from "@/theme"
import { renderIconFromIconProp } from "@/theme/icons/renderIconFromIconProp"
import { SHADOWS } from "@/theme/theme/shadows"
import { useRouter, type Href } from "expo-router"
import React from "react"
import type { PressableProps } from "react-native"
import { StyleSheet, Pressable, View, Text } from "react-native"

interface FabProps extends WithOptionalTestId<PressableProps> {
  IconComponent: IconProp
  color?: string
  position?: "bottom-left" | "bottom-right"
  size?: number
  text?: string
}

const TEXT_COLOR = COLORS.$textDark.primary

const FloatingActionButton = React.forwardRef<View, FabProps>(
  function FloatingActionButton(
    { IconComponent, position = "bottom-right", size = 56, text, ...rest },
    ref,
  ) {
    return (
      <Pressable
        ref={ref}
        style={({ pressed }) => [
          styles.root,
          position === "bottom-right"
            ? styles.positionRight
            : styles.positionLeft,
          {
            height: size,
            opacity: pressed ? 0.5 : 1,
          },
        ]}
        {...rest}
      >
        <View style={styles.contentContainer}>
          {Boolean(IconComponent) && (
            <View style={styles.iconContainer}>
              {renderIconFromIconProp(IconComponent, {
                color: TEXT_COLOR,
              })}
            </View>
          )}
          {typeof text === "string" && <Text style={styles.text}>{text}</Text>}
        </View>
      </Pressable>
    )
  },
)

const styles = StyleSheet.create({
  contentContainer: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
  },
  iconContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  positionLeft: {
    left: SPACING.$5,
  },
  positionRight: {
    right: SPACING.$5,
  },
  root: {
    alignItems: "center",
    backgroundColor: COLORS.$primary[500],
    borderRadius: RADII.$full,
    bottom: SPACING.$5,
    justifyContent: "center",
    paddingHorizontal: SPACING.$4,
    position: "absolute",
    ...SHADOWS.$8,
  },
  text: {
    color: TEXT_COLOR,
    fontFamily: getFontName("Poppins_500Medium"),
    fontSize: 16,
    marginLeft: 8,
    textTransform: "uppercase", // Adds space between icon and label
  },
})

export default FloatingActionButton

export function FloatingActionButtonLink({
  href,
  ...props
}: FabProps & {
  href: Href
}) {
  const router = useRouter()
  return (
    <FloatingActionButton
      role={"link"}
      onPress={() => {
        router.navigate(href)
      }}
      {...props}
    />
  )
}
